import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./lib/i18n"; // Initialize i18n
import App from "./App.tsx";

/**
 * Enable MSW mocking conditionally based on environment variables
 * The worker intercepts requests before they go to the network
 *
 * MSW is disabled when:
 * - VITE_ENABLE_MSW is not set to "true"
 * - VITE_E2E_TEST is set to "true" (Playwright E2E tests use real backend)
 */
async function enableMocking() {
	// Always import MSW to prevent tree-shaking, then decide whether to start it
	const { worker } = await import("./mocks/browser");

	// Disable MSW during E2E tests (Playwright uses real backend)
	const isE2ETest = import.meta.env.VITE_E2E_TEST === "true";
	if (isE2ETest) {
		// biome-ignore lint/suspicious/noConsole: Informative log for E2E mode
		console.log("🧪 E2E test mode - Using real API");
		return;
	}

	// Only start MSW if explicitly enabled via environment variable
	const shouldEnableMSW = import.meta.env.VITE_ENABLE_MSW === "true";

	if (!shouldEnableMSW) {
		// biome-ignore lint/suspicious/noConsole: Informative log for dev mode
		console.log("🔌 Using real API -", import.meta.env.VITE_API_BASE_URL || "/api");
		return;
	}

	// biome-ignore lint/suspicious/noConsole: Informative log for dev mode
	console.log("🎭 Using MSW mocks");

	return worker.start({
		onUnhandledRequest: "bypass", // Don't warn about unhandled requests (assets, etc.)
	});
}

const rootElement = document.getElementById("root");
if (!rootElement) {
	throw new Error("Root element not found");
}

// Wait for MSW to be ready before rendering
void enableMocking().then(() => {
	createRoot(rootElement).render(
		<StrictMode>
			<App />
		</StrictMode>,
	);
});
